import {
  BreakpointQueryName,
  Carousel,
  Modal, Spacing, SpacingName, TypographyName,
} from '@doveit/lego';
import React from 'react';
import { NextPage } from 'next';
import { highlightToHTMLString } from '@doveit/hammer';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import { useTheme } from 'styled-components';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import BackBar from '../client/components/back-bar/BackBar';
import PageHead from '../client/components/page-head/PageHead';
import TagList from '../client/components/tag-list/TagList';
import CreateIntent from '../client/containers/create-web-intent/CreateWebIntent';
import { AppContextConsumer } from '../client/contexts/app-context/AppContext';
import { SERP_CONTENTS_BY_LOCALITY_SLUG } from '../client/graphql/queries/serpContents';
import withNextConfig from '../client/hocs/withNextConfig/withNextConfig';
import LayoutWrapper from '../client/layouts/layout-wrapper/LayoutWrapper';
import ListingLayout from '../client/layouts/listing-layout/ListingLayout';
import { sendEvent } from '../client/services/tracking/tracking';
import EnergySection from '../client/templates/common/energy-section/EnergySection';
import GallerySection, { GallerySectionItem } from '../client/templates/common/gallery-section/GallerySection';
import ListingHeader from '../client/templates/common/listing-header/ListingHeader';
import RapidLinks, { RapidLinksProps } from '../client/templates/common/rapid-links/RapidLinks';
import ContactBarMobile from '../client/templates/listing/contact-bar-mobile/ContactBarMobile';
import DescriptionSection from '../client/templates/listing/description-section/DescriptionSection';
import ListingDetails from '../client/templates/listing/listing-details/ListingDetails';
import ListingLocation from '../client/templates/listing/listing-location/ListingLocation';
import * as styles from '../client/templates/listing/Listing.style';
import { BANNER_SELL_PROPERTY } from '../client/themes/icons';
import { buildBreadcrumb } from '../client/utils/breadcrumb/properties';
import {
  buildAgentURLObject,
  buildListingURLObject,
  buildSerpURLObject,
  buildSoldPropertiesURLObject,
  goToPage,
  relativeToAbsoluteURL,
  SERP_URL,
  slugIt,
} from '../client/utils/links';
import { getMinSoldAtDate } from '../client/utils/search/filters';
import data from '../data/common.yml';
import { getBreadcrumbStructuredData, getPropertyStructuredData, getStructuredData } from '../data/structured';
import { Adv } from '../domain/adv';
import {
  AdvFluidType, Agent, RealEstateAgency, SerpContent,
} from '../domain/graphcms';
import { getLogger } from '../logger/isomorphic';
import { getAdvByType } from '../services/advs/advs';
import { Property, PropertySearchResult } from '../services/dtos/property';
import { getByIdV4, getSimilarTo } from '../services/properties/properties';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { AGENT_BY_EMAIL } from '../client/graphql/queries/agent';
import IntentBox from '../client/templates/common/intent-box/IntentBox';
import PageSection from '../client/components/page-section/PageSection';
import PropertyCard2 from '../client/components/property-card-2/PropertyCard2';
import { AgentCardProps } from '../client/components/agent-card/AgentCard';
import { Status } from '../domain/status';
import { NullPointerError } from '../client/utils/error/error';

const logger = getLogger();
const { intentPhone } = data;

export interface ListingPageProps {
  property: Property,
  agent?: AgentCardProps,
  adv: Adv,
  rapidLinks: RapidLinksProps,
  similarProperties?: PropertySearchResult[],
  config: {
    MAPS: {
      apiKey: string,
    },
  },
}

export interface ListingEvaluationState {
  contactModalOpen: boolean,
  openGalleryModal: GallerySectionItem,
}

export const ListingPage: NextPage<ListingPageProps> = ({
  property,
  agent,
  adv,
  rapidLinks,
  similarProperties = [],
  config,
}) => {
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [openGalleryModal, setOpenGalleryModal] = React.useState<GallerySectionItem | undefined>();

  const theme = useTheme();
  const isTabletOrBigger = useMediaQuery(theme.breakpoints.up(BreakpointQueryName.MD));

  const breadcrumb = React.useMemo(
    () => buildBreadcrumb({
      province: property.province,
      locality: property.locality,
      area: property.area,
      property,
    }),
    [property],
  );

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const openItem = urlParams.get('open') as GallerySectionItem;

    if (Object.values(GallerySectionItem).includes(openItem)) {
      setOpenGalleryModal(openItem);
    }
  }, []);

  const onSimilarPropertyClick = React.useCallback((similar: PropertySearchResult) => () => {
    goToPage(buildListingURLObject(similar));
  }, []);

  const toggleContactModal = React.useCallback(
    () => setContactModalOpen(!contactModalOpen),
    [contactModalOpen],
  );

  const backToSerp = React.useCallback((previousURLObject) => () => {
    const urlObject = buildSerpURLObject();

    if (!previousURLObject || (previousURLObject && previousURLObject.url.pathname !== SERP_URL)) {
      return goToPage(urlObject);
    }

    if (previousURLObject.url.pathname === SERP_URL && previousURLObject.url.query.locality) {
      return goToPage(previousURLObject);
    }

    return goToPage(urlObject);
  }, []);

  const renderHeader = React.useCallback(() => (
    <ListingHeader
      title={property.title}
      subtitle={property.suburb}
      price={property.price}
      highlights={property.highlights.slice(0, 5).map((h) => highlightToHTMLString(h))}
    />
  ), [property]);

  const renderSecondaryNavigation = React.useCallback(() => (
    <div css={styles.backBar}>
      <LayoutWrapper>
        <AppContextConsumer>
          {({ previousURLObject }) => (
            <BackBar
              backButton={{
                label: 'Case in vendita',
                onClick: backToSerp(previousURLObject),
                href: '/vendita-case',
                'aria-label': 'Torna all\'elenco delle case in vendita',
                title: 'Torna all\'elenco delle case in vendita',
              }}
            />
          )}
        </AppContextConsumer>
      </LayoutWrapper>
    </div>
  ), [backToSerp]);

  const renderSide = React.useCallback(() => (
    <div css={styles.secondaryWrapper}>
      <IntentBox
        reference={property.reference}
        agent={agent}
      >
        <CreateIntent
          id={property.id}
          formProps={{ dataRef: 'intent-form-desktop' }}
          onSuccess={(intent) => sendEvent(
            'intent_submit',
            {
              category: 'intent',
              action: 'submit',
              label: 'side:intent-form',
              intentId: intent.id,
            },
          )}
        />
      </IntentBox>
    </div>
  ), [agent, property.id, property.reference]);

  return (
    <ListingLayout
      render={{
        secondaryNavigation: renderSecondaryNavigation,
        header: renderHeader,
        side: renderSide,
      }}
      breadcrumb={breadcrumb}
    >
      <PageHead
        SEO={{
          ...property.SEO,
          canonical: relativeToAbsoluteURL(property.relativePath),
        }}
        image={{ url: property.SEO.previewUrl }}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
          getPropertyStructuredData(property),
        )}
      />
      <GallerySection
        {...property}
        hasOffer={property.status === Status.PROPOSTA}
        allowedItems={
          Object.values(GallerySectionItem).filter((item) => (
            item === GallerySectionItem.STREET_VIEW ? !property.privacy.includes('VIRTUAL_MAP') : true))
        }
        current={openGalleryModal}
      />

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Composizione
          </PageSection.Title>
          <PageSection.Block>
            {property.composition}
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Dettagli immobile
          </PageSection.Title>
          <PageSection.Block>
            <ListingDetails details={property.details} />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Caratteristiche
          </PageSection.Title>
          <PageSection.Block>
            <TagList list={property.features} />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Descrizione
          </PageSection.Title>
          <PageSection.Block>
            <DescriptionSection
              abstract={property.abstract}
              description={property.description}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Efficienza energetica
          </PageSection.Title>
          <PageSection.Block>
            <EnergySection
              energyClass={property.energyClass}
              energyValue={property.energyValue}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      {adv && (
        <div css={styles.itemAdv}>
          <AdvFluid
            title={adv.title}
            description={adv.description}
            cta={adv.cta}
            urlObject={adv.urlObject}
            symbolPath={BANNER_SELL_PROPERTY}
            dataRef="adv-fluid-v1-main-sell-properties-listing"
            dataTracking="adv-fluid-v1-main-sell-properties-listing"
          />
        </div>
      )}

      <PageSection padding={[SpacingName.BIG, 0, 0]}>
        <PageSection.Wrapper withoutPad>
          <PageSection.Title
            size={TypographyName.H4}
            withBorder
          >
            Posizione immobile
          </PageSection.Title>
          <PageSection.Block>
            <ListingLocation
              apiKey={config.MAPS.apiKey}
              route={property.route}
              normalizedAddress={property.normalizedAddress}
              suburb={property.suburb}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <div
        css={styles.requestInfo}
        data-ref="request-info-section"
      >
        <PageSection>
          <PageSection.Wrapper withoutPad>
            <PageSection.Title
              size={TypographyName.H4}
              withBorder
            >
              Richiesta informazioni
            </PageSection.Title>
            <PageSection.Block>
              <IntentBox
                reference={property.reference}
                agent={agent}
                minimal
              >
                <CreateIntent
                  id={property.id}
                  formProps={{ dataRef: 'intent-form-mobile' }}
                  onSuccess={(intent) => sendEvent(
                    'intent_submit',
                    {
                      category: 'intent',
                      action: 'submit',
                      label: 'main:intent-form',
                      intentId: intent.id,
                    },
                  )}
                />
              </IntentBox>
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      </div>

      {similarProperties.length > 0 && (
        <PageSection>
          <PageSection.Wrapper withoutPad>
            <PageSection.Title
              size={TypographyName.H4}
              withBorder
            >
              Altri immobili che potrebbero interessarti
            </PageSection.Title>
            <PageSection.Block>
              {similarProperties.length === 1 && (
                <PropertyCard2
                  title={similarProperties[0].title}
                  price={similarProperties[0].price}
                  urlObject={buildListingURLObject(similarProperties[0])}
                  onClick={onSimilarPropertyClick(similarProperties[0])}
                  suburb={similarProperties[0].suburb}
                  features={similarProperties[0].highlights.map(highlightToHTMLString)}
                  cover={similarProperties[0].coverImageSet.medium}
                  gallery={(similarProperties[0].additionalCoverSet || []).map((c) => c.medium)}
                  description={similarProperties[0].abstract}
                  ariaLabel={similarProperties[0].title}
                  promoted={similarProperties[0].promoted}
                />
              )}

              {similarProperties.length > 1 && (
                <Carousel totalElements={similarProperties.length}>
                  <div css={styles.carouselWrapper}>
                    <Carousel.Items>
                      {similarProperties.map((similarProperty) => {
                        const {
                          id, coverImageSet, price, title, abstract, suburb, highlights, promoted, additionalCoverSet = [],
                        } = similarProperty;

                        return (
                          <Carousel.Item key={id}>
                            <PropertyCard2
                              title={title}
                              price={price}
                              urlObject={buildListingURLObject(similarProperty)}
                              onClick={onSimilarPropertyClick(similarProperty)}
                              suburb={suburb}
                              features={highlights.map(highlightToHTMLString)}
                              cover={coverImageSet.medium}
                              gallery={additionalCoverSet.map((c) => c.medium)}
                              description={abstract}
                              ariaLabel={title}
                              promoted={promoted}
                            />
                          </Carousel.Item>
                        );
                      })}
                    </Carousel.Items>
                  </div>

                  <div css={styles.carouselNavigationWrapper}>
                    {!isTabletOrBigger && <Carousel.Navigation />}
                    {isTabletOrBigger && (
                      <div css={styles.carouselButtonsWrapper}>
                        <Carousel.PrevButton />
                        <Carousel.NextButton />
                      </div>
                    )}
                  </div>

                </Carousel>
              )}
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      )}

      <Spacing margin={[SpacingName.BIG, 0]}>
        <RapidLinks
          withoutPad
          locality={rapidLinks.locality}
          title="Vuoi vendere casa?"
          showEvaluation={rapidLinks.showEvaluation}
          fallbackEvaluation
          showAgency={rapidLinks.showAgency}
        />
      </Spacing>

      <ContactBarMobile
        phone={intentPhone}
        emailAction={toggleContactModal}
      />

      <Modal
        open={contactModalOpen}
        onCloseHandler={toggleContactModal}
        data-ref="gallery-modal"
      >
        <Modal.Close />
        <Modal.Header title="Contattaci" />
        <Modal.Body padded>
          <CreateIntent
            id={property.id}
            formProps={{ dataRef: 'intent-form-mobile-modal' }}
            onSuccess={(intent) => sendEvent(
              'intent_submit',
              {
                category: 'intent',
                action: 'submit',
                label: 'modal:intent-form',
                intentId: intent.id,
              },
            )}
          />
        </Modal.Body>
      </Modal>
    </ListingLayout>
  );
};

export const getServerSideProps = withGetServerSideError<ListingPageProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const { id, locality, slug } = query;
  const currentPath = `/vendita-case/${locality}/${slug}/${id}`;

  const propertyId = id as string;

  const [property, adv, similarProperties] = await Promise.all([
    getByIdV4(parseInt(propertyId, 10)),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    getSimilarTo(parseInt(propertyId, 10))
      .catch((err) => {
        logger.error({ err });
        return [] as PropertySearchResult[];
      }),
  ]);
  if (!property) {
    logger.error({
      err: new NullPointerError(`Cannot find a property with id ${propertyId}`),
    },
    `Not valid property redirect to ${locality} serp page`);
    return {
      redirect: {
        destination: buildSerpURLObject({ locality: locality.toString() }).as,
        permanent: true,
      },
    };
  }

  if (property.soldAt && new Date(property.soldAt) < getMinSoldAtDate()) {
    return {
      redirect: {
        destination: buildSoldPropertiesURLObject().as,
        permanent: true,
      },
    };
  }

  if (currentPath !== property.relativePath) {
    return {
      redirect: {
        destination: property.relativePath,
        permanent: true,
      },
    };
  }

  const [serpContent, agent] = await Promise.all([
    await graphQLClient.query<{ serpContents: SerpContent[] }>({
      query: SERP_CONTENTS_BY_LOCALITY_SLUG,
      variables: {
        slug: query.locality,
      },
    })
      .then((result) => {
        const serpContents = result.data.serpContents || [];
        return serpContents[0];
      })
      .catch((err) => {
        logger.error({ err });
        return undefined;
      }),
    await graphQLClient.query<{ agent: Agent & { realEstateAgencies: RealEstateAgency[] } }>({
      query: AGENT_BY_EMAIL,
      variables: {
        email: property.agentEmail,
      },
    })
      // un agente non più attivo non viene mostrato nella scheda annuncio
      .then((result) => (result.data.agent?.active === false ? undefined : result.data.agent))
      .catch((err) => {
        logger.error({ err });
        return undefined;
      }),
  ]);

  return {
    props: {
      property,
      agent: agent ? {
        name: `${agent.name} ${agent.surname}`,
        jobTitle: agent.jobTitleType.name,
        picture: agent.photo,
        urlObject: agent.biography && buildAgentURLObject({ slug: agent.slug }),
        franchising: agent.realEstateAgencies?.filter((agency: RealEstateAgency) => agency.franchising).length > 0,
      } : null,
      adv,
      rapidLinks: {
        locality: {
          name: property.locality,
          slug: slugIt(property.locality),
        },
        showAgency: serpContent?.hasAgency ?? false,
        showEvaluation: serpContent?.hasEvaluation ?? false,
      } as RapidLinksProps,
      similarProperties,
    } as ListingPageProps,
  };
});

export default withError(withNextConfig(ListingPage));
